import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { formatQuantity } from "../lib/utils";
import type { FridgeItem, Ingredient } from "../types";

export default function FridgePage() {
  const qc = useQueryClient();
  const fridgeQ = useQuery({
    queryKey: ["fridge"],
    queryFn: () => api.get<{ items: FridgeItem[] }>("/api/fridge"),
  });
  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
  });

  const [addId, setAddId] = useState("");
  const [addQty, setAddQty] = useState<number | "">("");
  const [filter, setFilter] = useState("");

  const setQty = useMutation({
    mutationFn: ({ ingredientId, quantity }: { ingredientId: string; quantity: number }) =>
      api.put("/api/fridge", { ingredientId, quantity }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fridge"] }),
  });
  const remove = useMutation({
    mutationFn: (ingredientId: string) => api.delete(`/api/fridge?ingredientId=${ingredientId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fridge"] }),
  });

  const items = fridgeQ.data?.items ?? [];
  const allIngredients = ingredientsQ.data?.ingredients ?? [];
  const inFridgeIds = new Set(items.map((i) => i.ingredientId));
  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const f = filter.toLowerCase();
    return items.filter((i) => i.ingredient?.name.toLowerCase().includes(f));
  }, [items, filter]);

  return (
    <div>
      <PageHeader
        icon="🧊"
        title="Lednice"
        description="Aktuální zásoby. Nákupy a vaření je automaticky upravují, ručně můžete kdykoli opravit."
      />

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>➕</span>
          <span>Přidat zásobu ručně</span>
        </h2>
        <div className="grid sm:grid-cols-[1fr_140px_auto] gap-2 items-end">
          <div>
            <label className="label">Surovina</label>
            <select className="input" value={addId} onChange={(e) => setAddId(e.target.value)}>
              <option value="">— vyberte —</option>
              {allIngredients
                .filter((i) => !inFridgeIds.has(i.id))
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="label">Množství</label>
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={addQty}
              onChange={(e) => setAddQty(e.target.value === "" ? "" : Number(e.target.value))}
            />
          </div>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!addId || !addQty || Number(addQty) <= 0}
            onClick={() => {
              setQty.mutate(
                { ingredientId: addId, quantity: Number(addQty) },
                {
                  onSuccess: () => {
                    setAddId("");
                    setAddQty("");
                  },
                }
              );
            }}
          >
            <span aria-hidden>➕</span>
            <span>Přidat</span>
          </button>
        </div>
      </div>

      <input
        className="input max-w-md mb-4"
        placeholder="🔍 Hledat v lednici…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {filtered.length === 0 ? (
        <EmptyState
          icon={filter ? "🔍" : "🧊"}
          title={filter ? "Nic nenalezeno" : "Lednice je prázdná"}
          description={filter ? undefined : "Při nákupu se zásoby přidávají automaticky."}
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-2">Surovina</th>
                <th className="px-4 py-2">Množství</th>
                <th className="px-4 py-2 text-right">Akce</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((it) => (
                <FridgeRow
                  key={it.ingredientId}
                  item={it}
                  onSet={(q) => setQty.mutate({ ingredientId: it.ingredientId, quantity: q })}
                  onRemove={() => remove.mutate(it.ingredientId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FridgeRow({
  item,
  onSet,
  onRemove,
}: {
  item: FridgeItem;
  onSet: (q: number) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.quantity);
  const unit = item.ingredient?.unit ?? "";
  return (
    <tr>
      <td className="px-4 py-2 font-medium">{item.ingredient?.name ?? item.ingredientId}</td>
      <td className="px-4 py-2">
        {editing ? (
          <div className="flex gap-1 items-center">
            <input
              className="input w-24"
              type="number"
              step="0.01"
              min={0}
              value={val}
              onChange={(e) => setVal(Number(e.target.value))}
            />
            <span className="text-xs text-slate-500">{unit}</span>
          </div>
        ) : (
          formatQuantity(item.quantity, unit)
        )}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {editing ? (
          <>
            <button
              className="btn-primary text-xs mr-1 inline-flex items-center gap-1"
              title="Uložit množství"
              onClick={() => {
                onSet(val);
                setEditing(false);
              }}
            >
              <span aria-hidden>💾</span>
              <span>Uložit</span>
            </button>
            <button className="btn-ghost text-xs" onClick={() => setEditing(false)}>
              Zrušit
            </button>
          </>
        ) : (
          <>
            <button
              className="btn-secondary text-xs mr-1 inline-flex items-center gap-1"
              title="Upravit množství"
              onClick={() => {
                setVal(item.quantity);
                setEditing(true);
              }}
            >
              <span aria-hidden>✏️</span>
              <span>Upravit</span>
            </button>
            <button className="btn-ghost text-xs text-red-600 inline-flex items-center gap-1" title="Vyhodit z lednice" onClick={onRemove}>
              <span aria-hidden>🗑️</span>
              <span>Vyhodit</span>
            </button>
          </>
        )}
      </td>
    </tr>
  );
}
